import { SeOverlayOptions } from './overlay-options'
import { OverlayClasses } from './overlay-classes'

export class SeOverlay implements SeOverlayOptions {

  public element: HTMLElement
  public id: string
  public closeOnClick: boolean = true
  public waitForAnimationOnCloseCallback: boolean = true
  public onClose: () => void

  constructor(options: SeOverlayOptions, private animationDuration: number) {
    this.initializeFromOptions(options)
  }

  private initializeFromOptions(options: SeOverlayOptions): void {
    this.initializeId(options)
    this.initializeCloseOnClick(options)
    this.initializeCloseCallback(options)
    this.initializeWaitForAnimationOnCloseCallback(options)
  }

  public async open(): Promise<void> {

    this.initializeId({id: this.id}) // ensures the ID is still an available DOM ID before continuing
    this.initializeElement()
    await this.animateEntrance()

    if (this.closeOnClick) {
      this.addClickEventListenerForCloseOnClick()
    }

    this.freezeBody()

  }

  public async close(): Promise<void> {

    if (!this.element) {
      return
    }

    await this.animateExit(this.waitForAnimationOnCloseCallback)
    this.unfreezeBody()

    if (this.onClose) {
      this.onClose()
    }

  }

  private async animateEntrance(): Promise<void> {
    document.body.appendChild(this.element)
    await this.pauseForAnimationDuration()
    this.element.className = OverlayClasses.Block
  }

  private async animateExit(waitForAnimation: boolean = true): Promise<void> {

    this.element.className = OverlayClasses.Removing

    if (waitForAnimation) {
      await this.pauseForAnimationDuration()
    }

    this.element.remove()
    this.element = undefined

  }

  private pauseForAnimationDuration(): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), this.animationDuration)
    })
  }

  private addClickEventListenerForCloseOnClick(): void {
    this.element.addEventListener('click', () => this.close())
  }

  private freezeBody(): void {
    document.body.classList.add(OverlayClasses.FreezeBody)
  }

  private unfreezeBody(): void {
    document.body.classList.remove(OverlayClasses.FreezeBody)
  }

  private initializeElement(): void {
    this.element = document.createElement('div')
    this.element.id = this.id
    this.element.className = OverlayClasses.Adding
  }

  private initializeId(options: SeOverlayOptions): void {
    this.id = this.getId(options)
  }

  private initializeCloseOnClick(options: SeOverlayOptions): void {

    if (!options || !options.hasOwnProperty('closeOnClick')) {
      return
    }

    this.closeOnClick = options.closeOnClick

  }

  private initializeCloseCallback(options: SeOverlayOptions): void {
    this.onClose = (options && options.onClose) ? options.onClose : undefined
  }

  private initializeWaitForAnimationOnCloseCallback(options: SeOverlayOptions): void {

    if (!options || !options.hasOwnProperty('waitForAnimationOnCloseCallback')) {
      this.waitForAnimationOnCloseCallback = true
      return
    }

    this.waitForAnimationOnCloseCallback = options.waitForAnimationOnCloseCallback

  }

  private getId(options: SeOverlayOptions): string {

    let id = (options && options.id) ? options.id : OverlayClasses.Block

    // ensure an element with this ID does not already exist
    if (!document.getElementById(id)) {
      return id
    }

    // try appending a timestamp to see if this will make the ID unique, if it doesn't, give up
    id += `-${Date.now()}`
    if (document.getElementById(id)) {
      return id
    }

    console.warn('could not create unique id for overlay')
    return undefined

  }

}
